import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ✅ کلید سبد به‌ازای هر کاربر تا سبد کاربران روی یک مرورگر با هم قاطی نشود
const currentUserKey = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user'));
    return u && (u.phone || u.id) ? String(u.phone || u.id) : 'guest';
  } catch {
    return 'guest';
  }
};
const CART_KEY = () => `cart_v2__${currentUserKey()}`;

// ── normalize محصول از API ─────────────────────────────────────
const normalizeCartProduct = (p) => {
  const host = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api$/, '');  const img  = p.main_image || p.image || '';
  // p.price از normalizeProduct میاد، current_price مستقیم از API
  const price = Number(
    p.discounted_price || p.price || p.current_price || p.original_price || 0
  );
  // موجودی محصول (stock) — اگر null/undefined بود، Infinity در نظر گرفته می‌شود (نامحدود)
  const rawStock = p.stock ?? p.quantity ?? p.available ?? null;
  const stock = (rawStock === null || rawStock === undefined || rawStock === '')
    ? Infinity
    : Number(rawStock);
  return {
    id:       p.id,
    name:     p.name || '',
    weight:   p.weight || 0,
    karat:    p.karat  || 18,
    category: p.category?.name || p.category || '',
    image:    img ? (img.startsWith('http') ? img : `${host}${img}`) : '',
    price,
    stock,
    slug:     p.slug || '',
  };
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems]   = useState([]);
  const [isLoading, setIsLoading]   = useState(false);

  // ── بارگذاری از localStorage ──────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CART_KEY()); // سبد مخصوص همین کاربر
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setCartItems(parsed);
      }
    } catch {}
  }, []);

  // ── ذخیره در localStorage ─────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(CART_KEY(), JSON.stringify(cartItems));
    }, 300);
    return () => clearTimeout(timer);
  }, [cartItems]);

  // ── افزودن محصول (با fetch از API) ───────────────────────────
  const addToCart = useCallback(async (productId, productData = null) => {
    setIsLoading(true);
    try {
      let product = productData;

      // اگه داده محصول مستقیم داده شده استفاده کن
      // وگرنه از API بگیر
      if (!product) {
        const res  = await fetch(`${API_BASE}/products/${productId}/`);
        if (!res.ok) throw new Error('محصول یافت نشد');
        const data = await res.json();
        product = normalizeCartProduct(data);
      } else {
        product = normalizeCartProduct(product);
      }

      // ✅ بررسی موجودی محصول
      const stockLimit = product.stock;
      if (stockLimit !== Infinity && stockLimit <= 0) {
        setIsLoading(false);
        return { success: false, message: 'این محصول در حال حاضر موجود نیست' };
      }

      let stockExceeded = false;
      setCartItems(prev => {
        const idx = prev.findIndex(item => item.product.id === product.id);
        if (idx >= 0) {
          const current = prev[idx].quantity;
          // اگه افزایش بیشتر از موجودی می‌شه، اجازه نده
          if (stockLimit !== Infinity && current + 1 > stockLimit) {
            stockExceeded = true;
            return prev;
          }
          const updated = [...prev];
          updated[idx] = { ...updated[idx], quantity: current + 1 };
          return updated;
        }
        return [...prev, {
          id:            Date.now(),
          product:       product,
          price_at_time: product.price,
          quantity:      1,
        }];
      });

      if (stockExceeded) {
        setIsLoading(false);
        return {
          success: false,
          message: `تعداد سبد به سقف موجودی (${stockLimit} عدد) رسیده است`
        };
      }

      // ✅ اعلان: اضافه به سبد
      try {
        window.dispatchEvent(new CustomEvent('notify', {
          detail: {
            type: 'cart',
            title: 'افزوده به سبد خرید',
            message: `«${product.name || 'محصول'}» به سبد خرید شما اضافه شد`,
          }
        }));
      } catch (_) { /* silent */ }

      setIsLoading(false);
      return { success: true, message: 'محصول به سبد خرید اضافه شد' };
    } catch (err) {
      setIsLoading(false);
      return { success: false, message: err.message || 'خطا در افزودن محصول' };
    }
  }, []);

  // ── افزایش تعداد ──────────────────────────────────────────────
  const increaseQuantity = useCallback((itemId) => {
    let stockExceededName = null;
    setCartItems(prev =>
      prev.map(item => {
        if (item.id !== itemId) return item;
        const stockLimit = item.product?.stock;
        // چک کن از موجودی فراتر نره
        if (stockLimit && stockLimit !== Infinity && item.quantity + 1 > stockLimit) {
          stockExceededName = item.product?.name || 'محصول';
          return item;  // بدون تغییر
        }
        return { ...item, quantity: item.quantity + 1 };
      })
    );
    // اگه فراتر رفت، اعلان نشون بده
    if (stockExceededName) {
      try {
        window.dispatchEvent(new CustomEvent('notify', {
          detail: {
            type: 'cart',
            title: 'سقف موجودی',
            message: `تعداد «${stockExceededName}» به سقف موجودی رسیده است`,
          }
        }));
      } catch (_) {}
    }
  }, []);

  // ── کاهش تعداد ───────────────────────────────────────────────
  const decreaseQuantity = useCallback((itemId) => {
    setCartItems(prev =>
      prev.map(item =>
        item.id === itemId && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      ).filter(item => !(item.id === itemId && item.quantity <= 1))
    );
  }, []);

  // ── تنظیم تعداد دقیق (set به مقدار مشخص) ─────────────────────
  const setQuantity = useCallback((itemId, newQuantity) => {
    const qty = Number(newQuantity);
    // اگر تعداد به صفر یا کمتر رسید، آیتم حذف می‌شود
    if (!qty || qty < 1) {
      setCartItems(prev => prev.filter(item => item.id !== itemId));
      return;
    }
    let stockExceededName = null;
    let cappedAt = null;
    setCartItems(prev =>
      prev.map(item => {
        if (item.id !== itemId) return item;
        const stockLimit = item.product?.stock;
        // اگه تعداد درخواستی بیشتر از موجودی هست، روی سقف cap کن
        if (stockLimit && stockLimit !== Infinity && qty > stockLimit) {
          stockExceededName = item.product?.name || 'محصول';
          cappedAt = stockLimit;
          return { ...item, quantity: stockLimit };
        }
        return { ...item, quantity: qty };
      })
    );
    if (stockExceededName) {
      try {
        window.dispatchEvent(new CustomEvent('notify', {
          detail: {
            type: 'cart',
            title: 'سقف موجودی',
            message: `«${stockExceededName}» تنها ${cappedAt} عدد در انبار موجود است`,
          }
        }));
      } catch (_) {}
    }
  }, []);

  // ── حذف آیتم ─────────────────────────────────────────────────
  const removeFromCart = useCallback((itemId) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  // ── خالی کردن سبد ────────────────────────────────────────────
  const clearCart = useCallback(() => {
    setCartItems([]);
    localStorage.removeItem(CART_KEY());
  }, []);

  // ── محاسبات ───────────────────────────────────────────────────
  const totalItems  = cartItems.reduce((s, i) => s + i.quantity, 0);
  const totalPrice  = cartItems.reduce((s, i) => s + (i.price_at_time * i.quantity), 0);
  const calculateTotal = () => totalPrice;  // سازگاری با کدهای قدیمی
  const updateCartItem = setQuantity;       // ✅ حالا تعداد دقیق را تنظیم می‌کند (هم کم هم زیاد)
  const fetchCart   = () => {};

  return (
    <CartContext.Provider value={{
      cartItems,
      isLoading,
      totalItems,
      cartCount: totalItems,
      totalPrice,
      calculateTotal,
      updateCartItem,
      setQuantity,
      addToCart,
      removeFromCart,
      increaseQuantity,
      decreaseQuantity,
      clearCart,
      fetchCart,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export default CartContext;








